import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import './SleepScreen.css';

class Particle {
  constructor(canvasWidth, canvasHeight) {
    this.canvasWidth = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.reset();
  }

  reset() {
    // Coordenadas 3D relativas al centro (0,0,0)
    // El eje Z va de 1000 (fondo) a 0 (cámara)
    this.x = (Math.random() - 0.5) * 2000;
    this.y = (Math.random() - 0.5) * 2000;
    this.z = 1000;

    this.vz = -2 - Math.random() * 5; // Velocidad hacia la cámara
    this.size = 0;
    this.projectedX = 0;
    this.projectedY = 0;

    this.isExploding = false;
    this.explosionRadius = 0;
    this.alpha = 1;
    this.char = 'Q';
  }

  update() {
    if (this.isExploding) {
      this.explosionRadius += 5;
      this.alpha -= 0.05;
      if (this.alpha <= 0) {
        this.reset();
      }
      return;
    }

    this.z += this.vz;

    // Proyección simple 3D -> 2D
    const focalLength = 400;
    const scale = focalLength / (focalLength + this.z);

    this.projectedX = (this.canvasWidth / 2) + (this.x * scale);
    this.projectedY = (this.canvasHeight / 2) + (this.y * scale);
    this.size = scale * 100;

    // Si la partícula pasa la cámara o sale de los bordes significativamente
    if (this.z <= -focalLength ||
        this.projectedX < -100 || this.projectedX > this.canvasWidth + 100 ||
        this.projectedY < -100 || this.projectedY > this.canvasHeight + 100) {
      this.reset();
    }
  }

  draw(ctx) {
    if (this.isExploding) {
      ctx.beginPath();
      ctx.arc(this.projectedX, this.projectedY, this.explosionRadius, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(100, 200, 255, ${this.alpha})`;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Dibujar letras Q desvaneciéndose
      ctx.fillStyle = `rgba(255, 255, 255, ${this.alpha})`;
      ctx.font = `${this.size}px monospace`;
      ctx.fillText(this.char, this.projectedX - (this.size/2), this.projectedY + (this.size/2));
      return;
    }

    ctx.fillStyle = `rgba(255, 255, 255, ${Math.min(1, 1000/this.z * 0.2)})`;
    ctx.font = `${this.size}px monospace`;
    ctx.fillText(this.char, this.projectedX - (this.size/2), this.projectedY + (this.size/2));
  }

  explode() {
    if (!this.isExploding) {
      this.isExploding = true;
      return true;
    }
    return false;
  }
}

const SleepScreen = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const requestRef = useRef();

  const [score, setScore] = useState(0);
  const scoreRef = useRef(0);
  const [isNeuroSymbioticSessionActive, setIsNeuroSymbioticSessionActive] = useState(false);
  const [dreamMessages, setDreamMessages] = useState([]);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  const saveFinalScore = useCallback(async () => {
    const finalScore = scoreRef.current;
    if (finalScore > 0) {
      try {
        // SECURITY: Validamos que sea un entero antes de enviar
        const count = Math.floor(Number(finalScore));
        if (count > 0) {
          await api.post('/users/q-connections', { count });
        }
      } catch (err) {
        console.warn("[SleepScreen] Error al guardar conexiones finales:", err);
      }
    }
  }, []);

  useEffect(() => {
    // Bloquear scroll al montar
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    // Inicializar partículas
    const initParticles = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const count = 40;
      particlesRef.current = Array.from({ length: count }, () => new Particle(canvas.width, canvas.height));
    };

    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    const animate = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesRef.current.forEach(p => {
        p.update();
        p.draw(ctx);
      });

      requestRef.current = requestAnimationFrame(animate);
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => {
      // Restaurar scroll al desmontar
      document.body.style.overflow = originalStyle;
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(requestRef.current);
      saveFinalScore();
    };
  }, [saveFinalScore]);

  // Lógica de Dream Stream con Datos Reales
  useEffect(() => {
    const generateDreamData = () => {
      const baseData = [
        t('sleepScreen.stream.processingMemories'),
        t('sleepScreen.stream.remActive'),
        t('sleepScreen.stream.user', { username: user?.username || t('sleepScreen.guest') }),
        t('sleepScreen.stream.neuralNetworkOptimized')
      ];

      if (user?.qProfileParams) {
        const params = user.qProfileParams;
        if (params.temp !== undefined) baseData.push(t('sleepScreen.stream.tempAssimilated', { value: params.temp }));
        if (params.learningRate !== undefined) baseData.push(t('sleepScreen.stream.learningRateCalibrated', { value: params.learningRate }));
        if (params.instinct !== undefined) baseData.push(t('sleepScreen.stream.instinctLatent', { value: params.instinct }));
        if (params.optimism) baseData.push(t('sleepScreen.stream.optimismActive'));
        if (params.sarcasm) baseData.push(t('sleepScreen.stream.sarcasmSynced'));
        if (params.overthinking) baseData.push(t('sleepScreen.stream.overthinkingAnalyzed'));
      }

      if (user?.sphereMode) {
        baseData.push(t('sleepScreen.stream.sphereMode', { mode: user.sphereMode }));
      }

      return baseData;
    };

    const dreamData = generateDreamData();

    const interval = setInterval(() => {
      setDreamMessages(prev => {
        const nextMsg = dreamData[Math.floor(Math.random() * dreamData.length)];
        const newMessages = [...prev, { id: Date.now() + Math.random(), text: nextMsg }];
        if (newMessages.length > 3) {
          return newMessages.slice(1);
        }
        return newMessages;
      });
    }, 3000);

    return () => clearInterval(interval);
  }, [t, user]);

  const handleCanvasClick = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    particlesRef.current.forEach(p => {
      if (!p.isExploding) {
        const dist = Math.sqrt((p.projectedX - mouseX) ** 2 + (p.projectedY - mouseY) ** 2);
        if (dist < p.size) {
          if (p.explode()) {
            if (!isNeuroSymbioticSessionActive) {
              setIsNeuroSymbioticSessionActive(true);
            }
            setScore(s => s + 10);
            startChainReaction(p);
          }
        }
      }
    });
  };

  const startChainReaction = (sourceParticle) => {
    // Requerimiento 1: Radio reducido a 60 para mejor balance
    const explosionRadius = 60;

    particlesRef.current.forEach(p => {
      if (!p.isExploding) {
        const dist = Math.sqrt((p.projectedX - sourceParticle.projectedX) ** 2 + (p.projectedY - sourceParticle.projectedY) ** 2);
        if (dist < explosionRadius) {
          // Pequeño delay para el efecto visual de cadena
          setTimeout(() => {
            if (p.explode()) {
              setScore(s => s + 5);
              startChainReaction(p);
            }
          }, 100);
        }
      }
    });
  };

  return (
    <motion.div
      className="sleep-screen-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8 }}
    >
      <canvas
        ref={canvasRef}
        className="particles-canvas"
        onClick={handleCanvasClick}
      />

      {isNeuroSymbioticSessionActive && (
        <div className="score-counter">
          <span className="score-label">{t('sleepScreen.qConnections')}</span>
          <span className="score-value">{score}</span>
        </div>
      )}

      <div className="sleep-content">
        <motion.div
          className="breathing-orb"
          animate={{
            scale: [0.85, 1.30, 0.85],
            opacity: [0.4, 0.7, 0.4],
            boxShadow: [
              "0 0 50px rgba(100, 200, 255, 0.2)",
              "0 0 80px rgba(100, 200, 255, 0.4)",
              "0 0 50px rgba(100, 200, 255, 0.2)"
            ]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <h1 className="sleep-title">{t('sleepScreen.title')}</h1>
        <p className="sleep-subtitle">{t('sleepScreen.subtitle')}</p>

        <div className="dream-stream">
          <AnimatePresence>
            {dreamMessages.map((msg) => (
              <motion.div
                key={msg.id}
                className="dream-message"
                initial={{ opacity: 0, y: 20, filter: 'blur(10px)' }}
                animate={{ opacity: 0.8, y: 0, filter: 'blur(0px)' }}
                exit={{ opacity: 0, y: -20, filter: 'blur(10px)' }}
                transition={{ duration: 1.5 }}
              >
                {msg.text}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        <div className="stars-container">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              className={`star star-${i+1}`}
              animate={{ opacity: [0, 0.5, 0] }}
              transition={{
                duration: 5,
                repeat: Infinity,
                delay: i,
                ease: "linear"
              }}
            />
          ))}
        </div>
      </div>
    </motion.div>
  );
};

export default SleepScreen;
